import React, { useEffect, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { IoWarning, IoDownloadOutline } from "react-icons/io5";
import { BsPlay, BsPause } from "react-icons/bs";
import { formatDate, formatTimer } from "src/utils";
import { useSendMessageMutation } from "src/store/services/disputeApi";

const getFileNameFromUrl = (url: string) => {
	try {
		const urlParts = url.split("/");
		return urlParts[urlParts.length - 1];
	} catch (error) {
		return "file";
	}
};
const convertLinksToAnchors = (text: string) => {
	const urlRegex = /(https?:\/\/[^\s]+)/g;
	return text.replace(
		urlRegex,
		(url) =>
			`<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`
	);
};

const renderMessageContent = (
	messageObj: any,
	audioRefs: any,
	playingAudio: any,
	setPlayingAudio: any,
	audioProgress: any,
	setAudioProgress: any
) => {
	const { message_type, message, url, _id, sender } = messageObj;
	if (!message_type || message_type === "text") {
		return (
			<div
				className='text-sm leading-6 break-words'
				dangerouslySetInnerHTML={{ __html: convertLinksToAnchors(message) }}
			/>
		);
	}
	if (message_type === "image") {
		return (
			<div className='relative max-w-[220px] rounded-lg overflow-hidden mb-1'>
				<img src={url} alt='Image' className='w-full rounded-lg' />
				<button
					className='absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80'
					onClick={() => {
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", getFileNameFromUrl(url));
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}}
				>
					<IoDownloadOutline size={18} />
				</button>
			</div>
		);
	}
	if (message_type === "audio") {
		return (
			<div className='w-full max-w-[220px] flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2'>
				<button
					className={`flex items-center justify-center rounded-full w-8 h-8 ${
						sender === "artisan"
							? "bg-purple-600 text-white"
							: "bg-gray-300 text-purple-600"
					}`}
					onClick={() => {
						if (playingAudio === _id) {
							audioRefs.current[_id]?.pause();
							setPlayingAudio(null);
						} else {
							if (playingAudio && audioRefs.current[playingAudio]) {
								audioRefs.current[playingAudio]?.pause();
							}
							if (!audioRefs.current[_id]) {
								audioRefs.current[_id] = new Audio(url);
								audioRefs.current[_id].addEventListener("timeupdate", () => {
									const audio = audioRefs.current[_id];
									const progress = (audio.currentTime / audio.duration) * 100;
									setAudioProgress((prev: any) => ({
										...prev,
										[_id]: progress,
									}));
								});
								audioRefs.current[_id].addEventListener("ended", () => {
									setPlayingAudio(null);
									setAudioProgress((prev: any) => ({ ...prev, [_id]: 0 }));
								});
							}
							audioRefs.current[_id].play();
							setPlayingAudio(_id);
						}
					}}
				>
					{playingAudio === _id ? <BsPause size={18} /> : <BsPlay size={18} />}
				</button>
				<div className='flex-1 h-1.5 bg-gray-300 rounded-full relative'>
					<div
						className={`h-1.5 rounded-full ${
							sender === "artisan" ? "bg-purple-600" : "bg-gray-400"
						}`}
						style={{ width: `${audioProgress[_id] || 0}%` }}
					></div>
				</div>
				<button
					className='ml-2 text-gray-400 hover:text-purple-600'
					onClick={() => {
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", getFileNameFromUrl(url));
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}}
				>
					<IoDownloadOutline size={18} />
				</button>
			</div>
		);
	}
	// Other file types
	return (
		<a
			href={url}
			target='_blank'
			rel='noopener noreferrer'
			className='flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200'
		>
			<span className='text-lg'>📄</span>
			<span>{getFileNameFromUrl(url)}</span>
			<button
				className='ml-auto text-gray-400 hover:text-purple-600'
				onClick={(e) => {
					e.preventDefault();
					const link = document.createElement("a");
					link.href = url;
					link.setAttribute("download", getFileNameFromUrl(url));
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				}}
			>
				<IoDownloadOutline size={18} />
			</button>
		</a>
	);
};

const ChatCard = ({
	bookingDetails,
	dispute_id,
	resolved,
	messages,
	setMessage,
}: CardProp) => {
	const { handleSubmit, register, reset } = useForm<ChatProp>({
		mode: "onChange",
	});
	const messageRef = useRef<HTMLDivElement | null>(null);
	const audioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
	const [playingAudio, setPlayingAudio] = React.useState<string | null>(null);
	const [audioProgress, setAudioProgress] = React.useState<{
		[key: string]: number;
	}>({});
	const [sendMessage] = useSendMessageMutation();

	useEffect(() => {
		messageRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	const onSubmit: SubmitHandler<ChatProp> = async (data) => {
		reset();
		if (!dispute_id) return;
		try {
			const res: any = await sendMessage({
				disputeId: dispute_id,
				sender: "Admin",
				message: data.message,
			}).unwrap();
			setMessage(res?.payload?.data?.messages);
		} catch (err) {
			// ignore
		}
	};

	// Prepare messengers info
	const customerImg = bookingDetails?.user_meta?.display_picture;
	const vendorImg = bookingDetails?.artisan_meta?.display_picture;
	const messengers = {
		user_meta: bookingDetails?.user_meta || {},
		artisan_meta: bookingDetails?.artisan_meta || {},
	};

	return (
		<div className='bg-white rounded-lg overflow-hidden w-full flex flex-col'>
			{/* Header: Avatars and names */}
			<div className='border-b-[#E6E6E6] border-b py-5 px-7 flex items-center gap-3 relative'>
				<img
					src={customerImg}
					alt='customer'
					className='w-8 h-8 rounded-full border-2 border-white -mr-2 z-10'
				/>
				<img
					src={vendorImg}
					alt='vendor'
					className='w-8 h-8 rounded-full border-2 border-white z-0'
				/>
				<span className='ml-3 font-semibold text-gray-900'>
					{messengers.user_meta.first_name} &{" "}
					{messengers.artisan_meta.first_name}'s Chat
				</span>
				<div className='ml-auto flex flex-col justify-between text-right'>
					<h3 className='text-base text-[#4D4D4D]'>
						{formatDate(bookingDetails?.createdAt)}
					</h3>
					<h3 className='text-sm text-[#B3B3B3]'>
						{formatTimer(bookingDetails?.createdAt)}
					</h3>
				</div>
			</div>
			{/* Chat Area */}
			<div className='flex-1 overflow-y-auto px-6 py-6 bg-gray-50 space-y-6'>
				{messages && messages.length > 0 ? (
					<div className='flex flex-col gap-6'>
						{messages.map((message: any, idx: number) => {
							const isVendor = message?.sender === "artisan";
							return (
								<div
									ref={messageRef}
									key={message?._id || idx}
									className={`flex ${
										isVendor ? "items-start" : "flex-row-reverse items-end"
									}`}
								>
									{/* Avatar */}
									<img
										src={isVendor ? vendorImg : customerImg}
										alt='person'
										className='w-8 h-8 rounded-full object-cover mt-1'
									/>
									<div
										className={`flex flex-col max-w-[70%] ${
											isVendor ? "items-start ml-3" : "items-end mr-3"
										}`}
									>
										{/* Name */}
										<span className='text-xs font-semibold mb-1 text-purple-700'>
											{isVendor
												? messengers.artisan_meta.first_name
												: messengers.user_meta.first_name}
										</span>
										{/* Message bubble */}
										<div
											className={`mb-1 ${
												!message.message_type || message.message_type === "text"
													? isVendor
														? "bg-gray-100 text-gray-900"
														: "bg-purple-600 text-white"
													: isVendor
													? "bg-gray-100"
													: "bg-purple-600"
											} px-4 py-2 rounded-2xl shadow-sm text-sm break-words`}
											style={{
												borderBottomRightRadius: isVendor ? 16 : 4,
												borderBottomLeftRadius: isVendor ? 4 : 16,
											}}
										>
											{renderMessageContent(
												message,
												audioRefs,
												playingAudio,
												setPlayingAudio,
												audioProgress,
												setAudioProgress
											)}
										</div>
										{/* Timestamp */}
										<span
											className={`text-xs text-gray-400 mt-1 ${
												isVendor ? "text-left" : "text-right"
											}`}
										>
											{message.message_date}
										</span>
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div className='flex justify-center items-center py-10 px-5'>
						<span className='text-sm px-5 py-3 flex items-center leading-5 rounded-lg bg-gray-50 text-gray-500'>
							<IoWarning size={20} /> There are currently no available chat
						</span>
					</div>
				)}
			</div>
			{/* Input area */}
			{!resolved && (
				<div className='w-full flex justify-center py-5'>
					<form
						onSubmit={handleSubmit(onSubmit)}
						className='rounded-2xl max-w-[80%] w-full px-5 h-[50px] flex items-center bg-[#F2F2F2]'
					>
						<input
							type='text'
							{...register("message", { required: true })}
							className='border-none outline-none bg-transparent w-full'
							placeholder='type here...'
						/>
						<button type='submit'>
							<img
								src='/svgs/sendIcon.svg'
								alt=''
								className='w-[24px] cursor-pointer h-[24px]'
							/>
						</button>
					</form>
				</div>
			)}
		</div>
	);
};

export default ChatCard;
